/**
 * Billing catalog — mirrors wappin-backend / Wapinn tenant UI contracts.
 */

export const FEATURE_KEYS = Object.freeze({
  USERS: 'users',
  TEAMS: 'teams',
  BROADCASTS: 'broadcasts',
  TEMPLATES: 'templates',
  API: 'api',
  DEVELOPER_PLATFORM: 'developer_platform',
  WEBHOOKS: 'webhooks',
  MONITORING: 'monitoring',
  WORKFLOW_AUTOMATION: 'workflow_automation',
  AI: 'ai',
  SHARED_WHATSAPP: 'shared_whatsapp',
  OWN_WHATSAPP_CONNECT: 'own_whatsapp_connect',
  OWN_INSTAGRAM_CONNECT: 'own_instagram_connect',
  OWN_MESSENGER_CONNECT: 'own_messenger_connect',
});

export const LIMIT_KEYS = Object.freeze({
  USERS: 'users',
  TEAMS: 'teams',
  CHANNELS: 'channels',
  WHATSAPP_NUMBERS: 'whatsapp_numbers',
  INSTAGRAM_ACCOUNTS: 'instagram_accounts',
  MESSENGER_PAGES: 'messenger_pages',
  CONTACTS: 'contacts',
  TEMPLATES: 'templates',
  BROADCASTS: 'broadcasts',
  MESSAGES_PER_MONTH: 'messages_per_month',
  STORAGE_MB: 'storage_mb',
  API_CALLS: 'api_calls',
  AUTOMATION_RULES: 'automation_rules',
});

export const UNLIMITED_LIMIT = -1;

export const FEATURE_CATALOG = Object.freeze([
  { key: FEATURE_KEYS.USERS, label: 'Users' },
  { key: FEATURE_KEYS.TEAMS, label: 'Teams' },
  { key: FEATURE_KEYS.BROADCASTS, label: 'Broadcasts' },
  { key: FEATURE_KEYS.TEMPLATES, label: 'Templates' },
  { key: FEATURE_KEYS.API, label: 'API' },
  { key: FEATURE_KEYS.DEVELOPER_PLATFORM, label: 'Developer Platform' },
  { key: FEATURE_KEYS.WEBHOOKS, label: 'Webhooks' },
  { key: FEATURE_KEYS.MONITORING, label: 'Monitoring' },
  { key: FEATURE_KEYS.WORKFLOW_AUTOMATION, label: 'Workflow Automation' },
  { key: FEATURE_KEYS.AI, label: 'AI' },
  { key: FEATURE_KEYS.SHARED_WHATSAPP, label: 'Shared WhatsApp' },
  { key: FEATURE_KEYS.OWN_WHATSAPP_CONNECT, label: 'Own WhatsApp Connect' },
  { key: FEATURE_KEYS.OWN_INSTAGRAM_CONNECT, label: 'Own Instagram Connect' },
  { key: FEATURE_KEYS.OWN_MESSENGER_CONNECT, label: 'Own Messenger Connect' },
]);

export const LIMIT_CATALOG = Object.freeze([
  { key: LIMIT_KEYS.USERS, label: 'Users' },
  { key: LIMIT_KEYS.TEAMS, label: 'Teams' },
  { key: LIMIT_KEYS.CHANNELS, label: 'Channels' },
  { key: LIMIT_KEYS.WHATSAPP_NUMBERS, label: 'WhatsApp Numbers' },
  { key: LIMIT_KEYS.INSTAGRAM_ACCOUNTS, label: 'Instagram Accounts' },
  { key: LIMIT_KEYS.MESSENGER_PAGES, label: 'Messenger Pages' },
  { key: LIMIT_KEYS.CONTACTS, label: 'Contacts' },
  { key: LIMIT_KEYS.TEMPLATES, label: 'Templates' },
  { key: LIMIT_KEYS.BROADCASTS, label: 'Broadcasts' },
  { key: LIMIT_KEYS.MESSAGES_PER_MONTH, label: 'Messages Per Month' },
  { key: LIMIT_KEYS.STORAGE_MB, label: 'Storage (MB)' },
  { key: LIMIT_KEYS.API_CALLS, label: 'API Calls' },
  { key: LIMIT_KEYS.AUTOMATION_RULES, label: 'Automation Rules' },
]);

export function buildDefaultFeatures() {
  return FEATURE_CATALOG.map((entry) => ({
    featureKey: entry.key,
    enabled: ['users', 'teams', 'broadcasts', 'templates'].includes(entry.key),
  }));
}

export function buildDefaultLimits() {
  const defaults = {
    users: 5,
    teams: 2,
    channels: 1,
    whatsapp_numbers: 1,
    instagram_accounts: 1,
    messenger_pages: 1,
    contacts: 1000,
    templates: 20,
    broadcasts: 10,
    messages_per_month: 5000,
    storage_mb: 1024,
    api_calls: 0,
    automation_rules: 0,
  };
  return LIMIT_CATALOG.map((entry) => ({
    limitKey: entry.key,
    limitValue: defaults[entry.key] ?? 0,
  }));
}

export function buildFeaturesFromPlan(plan) {
  const featureMap = Object.fromEntries(
    (plan?.features || []).map((feature) => [feature.featureKey, feature.enabled]),
  );
  const isFree = String(plan?.code || '').toLowerCase() === 'free';
  return FEATURE_CATALOG.map((entry) => {
    if (featureMap[entry.key] === true) return { featureKey: entry.key, enabled: true };
    if (featureMap[entry.key] === false) return { featureKey: entry.key, enabled: false };
    const paidChannelDefault = !isFree && (
      entry.key === FEATURE_KEYS.OWN_WHATSAPP_CONNECT
      || entry.key === FEATURE_KEYS.OWN_INSTAGRAM_CONNECT
      || entry.key === FEATURE_KEYS.OWN_MESSENGER_CONNECT
    );
    return { featureKey: entry.key, enabled: paidChannelDefault };
  });
}

export function buildLimitsFromPlan(plan) {
  const limitMap = Object.fromEntries(
    (plan?.limits || []).map((limit) => [limit.limitKey, limit.limitValue]),
  );
  return LIMIT_CATALOG.map((entry) => ({
    limitKey: entry.key,
    limitValue: Number.isFinite(limitMap[entry.key]) ? limitMap[entry.key] : 0,
  }));
}

export function countEnabledFeatures(features = []) {
  return features.filter((feature) => feature.enabled).length;
}
